package org.example.project.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.example.project.enginemodule.ModulShopDashboardNavState
import org.example.project.service.LoginService
import org.example.project.settings.LogoSettings
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.bottom
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.cursor
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexShrink
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.left
import org.jetbrains.compose.web.css.lineHeight
import org.jetbrains.compose.web.css.maxHeight
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.overflowX
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.right
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun DashboardNetTopbar(
    topbarAutoHide: Boolean,
    isTopbarVisible: Boolean,
    categories: Map<String, List<All>>,
    popupScreens: List<All> = emptyList(),
    categoryIcons: Map<String, String> = emptyMap(),
    showNavigationIcons: Boolean = false,
    horizontalSubmenu: Boolean = true,
    openTopbarScreens: List<String>,
    selectedKey: String,
    activeCompanyLabel: String,
    activeContentLabel: String,
    monitorAvailable: Boolean,
    monitorEnabled: Boolean,
    dashboardMonitor: All,
    userFeedPushEnabled: Map<String, Boolean>,
    feedDefinitionsWithStatus: List<Pair<DashboardFeedDefinition, Boolean>>,
    onTopbarHoverExit: () -> Unit,
    onToggleTopbarScreen: (String, Boolean) -> Unit,
    onSelect: (String) -> Unit,
    onHome: () -> Unit,
    onOpenMonitorTarget: (String) -> Unit,
    onPin: (String) -> Unit,
    onFeedPushEnabledChange: (String, Boolean) -> Unit,
    onFeedSettingsSave: (Map<String, Boolean>) -> Unit,
    onPasswordChange: (() -> Unit)?,
    onLogout: () -> Unit
) {
    val currentUser = dashboardTopbarUserLabel()
    fun categoryForScreen(key: String): String? =
        categories.entries.firstOrNull { (_, screens) -> screens.any { it.key == key } }?.key

    val initialCategory = remember(categories, selectedKey) {
        categoryForScreen(selectedKey) ?: categories.keys.firstOrNull().orEmpty()
    }
    var activeCategory by remember(categories) { mutableStateOf(initialCategory) }
    var hoverCategory by remember(categories) { mutableStateOf<String?>(null) }
    var hoverCloseJob by remember(categories) { mutableStateOf<Job?>(null) }
    val scope = rememberCoroutineScope()
    val displayedCategory = hoverCategory ?: if (horizontalSubmenu) null else activeCategory
    fun openHoverCategory(title: String) {
        hoverCloseJob?.cancel()
        hoverCategory = title
    }
    fun scheduleHoverClose() {
        hoverCloseJob?.cancel()
        hoverCloseJob = scope.launch {
            delay(180)
            hoverCategory = null
        }
    }
    LaunchedEffect(selectedKey, categories) {
        if (horizontalSubmenu) {
            categoryForScreen(selectedKey)?.let { activeCategory = it }
        }
    }

    Div({
        style {
            position(Position.Fixed)
            top(if (topbarAutoHide && !isTopbarVisible) (if (horizontalSubmenu) (-144).px else (-92).px) else 0.px)
            left(0.px)
            right(0.px)
            height(if (horizontalSubmenu) 132.px else 80.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("z-index", DashboardTheme.NAVIGATION_LAYER_Z_INDEX.toString())
            property("user-select", "none")
            property("-webkit-user-select", "none")
            property("transition", "top 0.22s ease")
            overflow("visible")
        }
        onMouseLeave {
            if (topbarAutoHide) onTopbarHoverExit()
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                justifyContent(JustifyContent.Center)
                gap(6.px)
                position(Position.Relative)
                width(100.percent)
                height(100.percent)
                property("padding", "16px clamp(0px, 6vw, 122px) 6px clamp(0px, 6vw, 122px)")
                backgroundColor(Color.transparent)
                property("box-sizing", "border-box")
                overflow("visible")
            }
        }) {
            val openScreen: (All) -> Unit = { screen ->
                if (screen.screenMode == ScreenMode.POPUP) {
                    onToggleTopbarScreen(screen.key, screen.key in openTopbarScreens)
                } else {
                    onSelect(screen.key)
                    onPin(screen.key)
                }
            }
            if (horizontalSubmenu) {
                DashboardNetThreeRowHeader(
                    categories = categories,
                    popupScreens = popupScreens,
                    categoryIcons = categoryIcons,
                    showNavigationIcons = showNavigationIcons,
                    activeCategory = displayedCategory,
                    selectedKey = selectedKey,
                    openTopbarScreens = openTopbarScreens,
                    activeCompanyLabel = activeCompanyLabel,
                    activeContentLabel = activeContentLabel,
                    currentUser = currentUser,
                    monitorAvailable = monitorAvailable,
                    monitorEnabled = monitorEnabled,
                    dashboardMonitor = dashboardMonitor,
                    userFeedPushEnabled = userFeedPushEnabled,
                    feedDefinitionsWithStatus = feedDefinitionsWithStatus,
                    onHome = onHome,
                    onOpenMonitorTarget = onOpenMonitorTarget,
                    onOpenScreen = openScreen,
                    onHoverCategory = ::openHoverCategory,
                    onCloseHoverCategory = ::scheduleHoverClose,
                    onFeedPushEnabledChange = onFeedPushEnabledChange,
                    onFeedSettingsSave = onFeedSettingsSave,
                    onPasswordChange = onPasswordChange,
                    onLogout = onLogout
                )
            } else {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(26.px)
                        position(Position.Relative)
                        width(100.percent)
                        minHeight(58.px)
                        overflow("visible")
                    }
                }) {
                    DashboardNetCategoryNav(
                        categories = categories,
                        categoryIcons = categoryIcons,
                        showNavigationIcons = showNavigationIcons,
                        horizontalSubmenu = false,
                        activeCategory = displayedCategory,
                        selectedKey = selectedKey,
                        openTopbarScreens = openTopbarScreens,
                        submenuTopPx = 44,
                        onOpenScreen = openScreen,
                        onSelectCategory = { activeCategory = it },
                        onHoverCategory = ::openHoverCategory,
                        onCloseHoverCategory = ::scheduleHoverClose
                    )

                    DashboardNetCenter(
                        activeContentLabel = activeContentLabel,
                        onHome = onHome
                    )
                }
            }
        }
    }
}

private fun dashboardTopbarUserLabel(): String {
    val user = LoginService.getUser() ?: return "User"
    val firstInitial = user.vorname.firstOrNull { it.isLetter() }?.uppercaseChar()?.toString().orEmpty()
    val lastName = user.nachname
        .split(Regex("\\s+"))
        .filter { it.isNotBlank() }
        .joinToString(" ") { part ->
            val lower = part.lowercase()
            lower.replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
        }
    return when {
        firstInitial.isBlank() && lastName.isBlank() -> user.id.ifBlank { "User" }
        firstInitial.isBlank() -> lastName
        lastName.isBlank() -> firstInitial
        else -> "$firstInitial.$lastName"
    }
}

@Composable
private fun DashboardNetThreeRowHeader(
    categories: Map<String, List<All>>,
    popupScreens: List<All>,
    categoryIcons: Map<String, String>,
    showNavigationIcons: Boolean,
    activeCategory: String?,
    selectedKey: String,
    openTopbarScreens: List<String>,
    activeCompanyLabel: String,
    activeContentLabel: String,
    currentUser: String,
    monitorAvailable: Boolean,
    monitorEnabled: Boolean,
    dashboardMonitor: All,
    userFeedPushEnabled: Map<String, Boolean>,
    feedDefinitionsWithStatus: List<Pair<DashboardFeedDefinition, Boolean>>,
    onHome: () -> Unit,
    onOpenMonitorTarget: (String) -> Unit,
    onOpenScreen: (All) -> Unit,
    onHoverCategory: (String) -> Unit,
    onCloseHoverCategory: () -> Unit,
    onFeedPushEnabledChange: (String, Boolean) -> Unit,
    onFeedSettingsSave: (Map<String, Boolean>) -> Unit,
    onPasswordChange: (() -> Unit)?,
    onLogout: () -> Unit
) {

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.FlexStart)
            gap(6.px)
            width(100.percent)
            overflow("visible")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "minmax(0, clamp(260px, 34vw, 440px)) minmax(220px, 1fr) minmax(0, clamp(120px, 18vw, 280px))")
                alignItems(AlignItems.Center)
                property("gap", "clamp(12px, 2vw, 32px)")
                minHeight(52.px)
                width(100.percent)
                minWidth(0.px)
                overflow("visible")
            }
        }) {
            Div({
                style {
                    minWidth(0.px)
                    overflow("visible")
                }
            }) {
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    minWidth(0.px)
                    overflow("hidden")
                }
            }) {
                DashboardNetCenter(
                    activeContentLabel = activeContentLabel,
                    onHome = onHome,
                    absolute = false,
                    logoHeightPx = 50
                )
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.FlexEnd)
                    minWidth(0.px)
                    overflow("visible")
                }
            }) {
                DashboardNetIdentityStrip(
                    activeCompanyLabel = activeCompanyLabel,
                    currentUser = currentUser,
                    onUserClick = onPasswordChange
                )
            }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                width(100.percent)
                minWidth(0.px)
                minHeight(30.px)
                overflow("visible")
            }
        }) {
            DashboardNetCategoryNav(
                categories = categories,
                categoryIcons = categoryIcons,
                showNavigationIcons = showNavigationIcons,
                horizontalSubmenu = false,
                submenuHorizontal = false,
                categoryUnderline = false,
                alignRight = false,
                fitContent = true,
                activeCategory = activeCategory,
                selectedKey = selectedKey,
                openTopbarScreens = openTopbarScreens,
                submenuTopPx = 34,
                onOpenScreen = onOpenScreen,
                onSelectCategory = {},
                onHoverCategory = onHoverCategory,
                onCloseHoverCategory = onCloseHoverCategory
            )
        }
    }
}

@Composable
internal fun DashboardFooterControls(
    popupScreens: List<All>,
    openTopbarScreens: List<String>,
    topLeftOffsetPx: Double,
    activeCompanyLabel: String,
    currentUser: String,
    monitorAvailable: Boolean,
    monitorEnabled: Boolean,
    dashboardMonitor: All,
    userFeedPushEnabled: Map<String, Boolean>,
    feedDefinitionsWithStatus: List<Pair<DashboardFeedDefinition, Boolean>>,
    startScreenOptions: List<All>,
    selectedStartScreen: String,
    onOpenMonitorTarget: (String) -> Unit,
    onToggleTopbarScreen: (String, Boolean) -> Unit,
    onFeedPushEnabledChange: (String, Boolean) -> Unit,
    onFeedSettingsSave: (Map<String, Boolean>) -> Unit,
    onStartScreenSave: (String) -> Unit,
    onPasswordChange: (() -> Unit)?,
    onLogout: () -> Unit
) {
    var showUserActions by remember { mutableStateOf(false) }

    Div({
        style {
            position(Position.Fixed)
            right(20.px)
            top(0.px)
            property("width", "max-content")
            property("max-width", "calc(50vw - 40px)")
            height(MONITOR_SUMMARY_HEIGHT.px)
            property("z-index", DashboardTheme.NAVIGATION_LAYER_Z_INDEX.toString())
            overflow("visible")
        }
    }) {
        if (monitorAvailable && monitorEnabled) {
            DashboardMonitorDrawer(
                snapshot = dashboardMonitor,
                pushEnabledByFeed = userFeedPushEnabled,
                embeddedButton = true,
                leadingContent = {},
                onOpenTarget = onOpenMonitorTarget
            )
        } else {
            Div({
                style {
                    height(MONITOR_SUMMARY_HEIGHT.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(10.px)
                    property("border-bottom", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                }
            }) {}
        }
    }

    if (popupScreens.isNotEmpty()) {
        Div({
            style {
                position(Position.Fixed)
                right(20.px)
                top(0.px)
                width(420.px)
                height(50.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.FlexEnd)
                property("z-index", DashboardTheme.NAVIGATION_LAYER_Z_INDEX.toString())
                overflow("visible")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.FlexEnd)
                gap(10.px)
                property("width", "max-content")
                property("max-width", "100%")
                overflow("visible")
            }
        }) {
                popupScreens.forEachIndexed { index, screen ->
                    if (index > 0) {
                        Span({
                            style {
                                color(DashboardTheme.TEXT_MUTED)
                                fontSize(13.px)
                            }
                        }) { Text("|") }
                    }
                    DashboardNetPopupNavItem(
                        label = screen.label.ifBlank { screen.key },
                        iconPath = screen.icon.ifBlank { "images/step.svg" },
                        active = screen.key in openTopbarScreens,
                        onClick = { onToggleTopbarScreen(screen.key, screen.key in openTopbarScreens) }
                    )
                }
            }
        }
    }

    if (showUserActions) {
        DashboardUserActionsPopup(
            onPasswordChange = onPasswordChange?.let { openPassword ->
                {
                    showUserActions = false
                    openPassword()
                }
            },
            onLogout = {
                showUserActions = false
                onLogout()
            },
            onClose = { showUserActions = false }
        )
    }
}

@Composable
private fun DashboardNetModuleShopNavItem(
    label: String,
    active: Boolean,
    onClick: () -> Unit
) {
    Span({
        style {
            color(DashboardTheme.TEXT_LIGHT)
            fontSize(15.px)
            fontWeight("500")
            cursor("pointer")
            padding(6.px, 2.px)
            property("border-bottom", if (active) "2px solid ${UISettings.ACCENT}" else "2px solid transparent")
            property("transition", "border-color 0.18s ease")
            property("white-space", "nowrap")
        }
        onClick { onClick() }
    }) { Text(label) }
}

@Composable
private fun DashboardNetMonitorStrip(
    snapshot: All,
    onOpenTarget: (String) -> Unit
) {
    val latestEntry = snapshot.columnSettings.firstOrNull()
    var expanded by remember { mutableStateOf(false) }
    var infoVisible by remember { mutableStateOf(false) }

    LaunchedEffect(latestEntry?.id) {
        if (latestEntry != null) {
            infoVisible = true
            delay(3400)
            infoVisible = false
        }
    }

    Div({
        style {
            position(Position.Relative)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(14.px)
            minWidth(0.px)
            padding(7.px, 14.px)
            backgroundColor(Color("rgba(255,255,255,0.03)"))
            property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
            property("border-radius", if (expanded) "12px 12px 0 0" else "12px")
            property("box-sizing", "border-box")
            overflow("visible")
        }
    }) {
        snapshot.module.forEachIndexed { index, item ->
            val count = item.count
            DashboardNetMonitorChip(
                item = item,
                count = count,
                onOpenTarget = onOpenTarget
            )
            if (index < snapshot.module.lastIndex) {
                Span({
                    style {
                        color(DashboardTheme.TEXT_MUTED)
                        fontSize(12.px)
                    }
                }) { Text("|") }
            }
        }
        if (latestEntry != null) {
            Span({
                style {
                    color(DashboardTheme.TEXT_MUTED)
                    fontSize(12.px)
                    fontWeight("500")
                    property("white-space", "nowrap")
                    maxWidth(320.px)
                    overflow("hidden")
                    property("text-overflow", "ellipsis")
                    property("opacity", if (infoVisible) "1" else "0")
                    property("transform", if (infoVisible) "translateX(0)" else "translateX(-10px)")
                    property("transition", "opacity 0.22s ease, transform 0.22s ease")
                }
            }) {
                Text(dashboardMonitorEntryText(latestEntry))
            }
        }
        if (snapshot.columnSettings.isNotEmpty()) {
            Button({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    width(26.px)
                    height(26.px)
                    padding(0.px)
                    backgroundColor(Color.transparent)
                    border(0.px)
                    borderRadius(8.px)
                    cursor("pointer")
                    property("outline", "none")
                    property("transform", if (expanded) "rotate(180deg)" else "rotate(0deg)")
                    property("transition", "transform 0.18s ease, background 0.14s ease")
                }
                onClick { expanded = !expanded }
            }) {
                Img("images/down.svg") {
                    style {
                        width(18.px)
                        height(18.px)
                        property("filter", DashboardTheme.ICON_FILTER_WHITE)
                    }
                }
            }
        }
        if (snapshot.columnSettings.isNotEmpty()) {
            Div({
                style {
                    position(Position.Absolute)
                    left(0.px)
                    top(100.percent)
                    width(360.px)
                    maxHeight(if (expanded) 280.px else 0.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(0.px)
                    padding(if (expanded) 6.px else 0.px, 0.px)
                    property("background", DashboardTheme.PREMIUM_SURFACE_BG)
                    property("border-left", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                    property("border-right", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                    property("border-bottom", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                    property("border-top", "0")
                    property("border-radius", "0 0 14px 14px")
                    property("box-shadow", if (expanded) "0 14px 36px ${DashboardTheme.SURFACE_SHADOW}" else "none")
                    property("box-sizing", "border-box")
                    property("z-index", DashboardTheme.NAVIGATION_POPUP_Z_INDEX.toString())
                    property("transition", "max-height 0.22s ease, padding 0.22s ease, box-shadow 0.22s ease")
                    overflowY("hidden")
                    overflowX("hidden")
                }
            }) {
                snapshot.columnSettings.forEach { entry ->
                    DashboardNetMonitorDropdownRow(entry = entry, onOpenTarget = onOpenTarget)
                }
            }
        }
    }
}

@Composable
private fun DashboardNetMonitorChip(
    item: All,
    count: Int,
    onOpenTarget: (String) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            cursor("pointer")
            property("opacity", if (count > 0) "1" else "0.58")
        }
        onClick { onOpenTarget(item.screenKey) }
    }) {
        Img(item.icon) {
            style {
                width(20.px)
                height(20.px)
                flexShrink(0)
                property("filter", if (count > 0) DashboardTheme.ICON_FILTER_ACTIVE else DashboardTheme.ICON_FILTER_WHITE)
            }
        }
        Span({
            style {
                color(if (count > 0) DashboardTheme.ACCENT else DashboardTheme.TEXT_LIGHT)
                fontSize(13.px)
                fontWeight("700")
                minWidth(12.px)
            }
        }) { Text(count.toString()) }
    }
}

@Composable
private fun DashboardNetMonitorDropdownRow(
    entry: All,
    onOpenTarget: (String) -> Unit
) {
    var hovered by remember { mutableStateOf(false) }
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            width(100.percent)
            padding(9.px, 14.px)
            property("box-sizing", "border-box")
            cursor("pointer")
            if (hovered) {
                backgroundColor(UISettings.TABULAR_HOVER_BG)
            }
        }
        onMouseEnter { hovered = true }
        onMouseLeave { hovered = false }
        onClick { onOpenTarget(entry.screenKey) }
    }) {
        Img(entry.icon) {
            style {
                width(16.px)
                height(16.px)
                flexShrink(0)
                property("filter", DashboardTheme.ICON_FILTER_WHITE)
            }
        }
        Span({
            style {
                color(if (hovered) DashboardTheme.ACCENT else DashboardTheme.TEXT_LIGHT)
                fontSize(12.px)
                property("white-space", "nowrap")
                property("overflow", "hidden")
                property("text-overflow", "ellipsis")
            }
        }) { Text(dashboardMonitorEntryText(entry)) }
    }
}

private fun dashboardMonitorEntryText(entry: All): String =
    entry.label.ifBlank { "Info" } +
        entry.bemerkung.trim().takeIf { it.isNotBlank() }?.let { ": $it" }.orEmpty()

@Composable
private fun DashboardNetIdentityStrip(
    activeCompanyLabel: String,
    currentUser: String,
    centered: Boolean = false,
    onUserClick: (() -> Unit)? = null
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(if (centered) JustifyContent.Center else JustifyContent.FlexEnd)
            gap(8.px)
            property("width", "max-content")
            flexShrink(0)
            color(DashboardTheme.TEXT_LIGHT)
            fontSize(13.px)
            fontWeight("500")
            property("white-space", "nowrap")
        }
    }) {
        DashboardNetIdentityItem("images/unternehmen.svg", activeCompanyLabel)
        Span({
            style {
                color(DashboardTheme.TEXT_MUTED)
                fontSize(13.px)
            }
        }) { Text("|") }
        DashboardNetIdentityItem("images/user.svg", currentUser, onClick = onUserClick)
    }
}

@Composable
private fun DashboardNetIdentityItem(
    iconPath: String,
    label: String,
    onClick: (() -> Unit)? = null
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            property("width", "max-content")
            flexShrink(0)
            if (onClick != null) cursor("pointer")
        }
        if (onClick != null) onClick { onClick() }
    }) {
        Img(iconPath) {
            style {
                width(17.px)
                height(17.px)
                flexShrink(0)
                property("filter", DashboardTheme.ICON_FILTER_WHITE)
            }
        }
        Span({
            style {
                property("white-space", "nowrap")
                flexShrink(0)
            }
        }) { Text(label) }
    }
}

@Composable
private fun DashboardNetCategoryNav(
    categories: Map<String, List<All>>,
    categoryIcons: Map<String, String>,
    showNavigationIcons: Boolean,
    horizontalSubmenu: Boolean,
    submenuHorizontal: Boolean = horizontalSubmenu,
    categoryUnderline: Boolean = horizontalSubmenu,
    alignRight: Boolean = false,
    fitContent: Boolean = false,
    activeCategory: String?,
    selectedKey: String,
    openTopbarScreens: List<String>,
    submenuTopPx: Int,
    onOpenScreen: (All) -> Unit,
    onSelectCategory: (String) -> Unit,
    onHoverCategory: (String) -> Unit,
    onCloseHoverCategory: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Stretch)
            justifyContent(if (alignRight) JustifyContent.FlexEnd else JustifyContent.FlexStart)
            property("gap", "clamp(10px, 2vw, 30px)")
            minWidth(0.px)
            if (fitContent) {
                property("width", "max-content")
                property("max-width", "100%")
            } else {
                width(100.percent)
            }
            flexShrink(if (fitContent) 1 else 0)
            overflowX("visible")
            overflowY("visible")
        }
    }) {
        val moduleShopScreen = categories.values.flatten().firstOrNull { it.key == "Modul Shop" }
        categories.forEach { (title, screens) ->
            val dropdownScreens = screens.filter { it.screenMode != ScreenMode.POPUP && it.key != "Modul Shop" }
            val active = title == activeCategory
            Div({
                style {
                    position(Position.Relative)
                    display(DisplayStyle.Flex)
                    flexShrink(0)
                }
                if (!horizontalSubmenu) {
                    onMouseEnter { onHoverCategory(title) }
                    onMouseLeave { onCloseHoverCategory() }
                }
            }) {
                DashboardNetNavItem(
                    label = title,
                    iconPath = categoryIcons[title].orEmpty().ifBlank { "images/apps.svg" },
                    showIcon = showNavigationIcons,
                    active = active,
                    fontSizePx = 17,
                    underline = categoryUnderline,
                    markHover = false,
                    colorHoverText = true,
                    onClick = { if (horizontalSubmenu) onSelectCategory(title) }
                )
                if (active && dropdownScreens.isNotEmpty()) {
                    Div({
                        style {
                            position(Position.Absolute)
                            if (alignRight && !submenuHorizontal) {
                                right(0.px)
                            } else {
                                left(0.px)
                            }
                            top(submenuTopPx.px)
                            property("z-index", DashboardTheme.NAVIGATION_POPUP_Z_INDEX.toString())
                            if (!submenuHorizontal) {
                                width(260.px)
                                minWidth(260.px)
                                padding(6.px, 0.px)
                                property("background", DashboardTheme.PREMIUM_SURFACE_BG)
                                property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                                property("border-radius", "14px")
                                property("box-shadow", "0 14px 36px ${DashboardTheme.SURFACE_SHADOW}")
                                property("box-sizing", "border-box")
                                overflow("hidden")
                            } else {
                                overflow("visible")
                            }
                        }
                        if (!horizontalSubmenu) {
                            onMouseEnter { onHoverCategory(title) }
                            onMouseLeave { onCloseHoverCategory() }
                        }
                    }) {
                        DashboardNetScreenNav(
                            screens = dropdownScreens.distinctBy { it.key },
                            selectedKey = selectedKey,
                            openTopbarScreens = openTopbarScreens,
                            showNavigationIcons = showNavigationIcons,
                            horizontal = submenuHorizontal,
                            underline = submenuHorizontal,
                            hoverPanelRows = !submenuHorizontal,
                            onOpenScreen = onOpenScreen
                        )
                    }
                }
            }
        }
        if (moduleShopScreen != null && !horizontalSubmenu) {
            DashboardNetModuleShopCategoryItem(
                label = moduleShopScreen.label.ifBlank { "Modul Shop" },
                iconPath = moduleShopScreen.icon.ifBlank { "images/modulshop.svg" },
                active = selectedKey == moduleShopScreen.key &&
                    ModulShopDashboardNavState.selectedTab == ModulShopDashboardNavState.SHOP &&
                    !ModulShopDashboardNavState.detailOpen,
                onClick = {
                    ModulShopDashboardNavState.openShop()
                    onOpenScreen(moduleShopScreen)
                }
            )
            DashboardNetModuleShopCategoryItem(
                label = "Meine Inhalte",
                iconPath = "images/box.svg",
                active = selectedKey == moduleShopScreen.key &&
                    ModulShopDashboardNavState.selectedTab == ModulShopDashboardNavState.MINE &&
                    !ModulShopDashboardNavState.detailOpen,
                onClick = {
                    ModulShopDashboardNavState.openMine()
                    onOpenScreen(moduleShopScreen)
                }
            )
        }
    }
}

@Composable
private fun DashboardNetModuleShopCategoryItem(
    label: String,
    iconPath: String,
    active: Boolean,
    onClick: () -> Unit
) {
    Div({
        style {
            position(Position.Relative)
            display(DisplayStyle.Flex)
            flexShrink(0)
        }
    }) {
        DashboardNetNavItem(
            label = label,
            iconPath = iconPath,
            showIcon = true,
            active = active,
            fontSizePx = 17,
            underline = true,
            markHover = false,
            colorHoverText = false,
            onClick = onClick
        )
    }
}

@Composable
private fun DashboardNetCenter(
    activeContentLabel: String,
    onHome: () -> Unit,
    absolute: Boolean = true,
    logoHeightPx: Int = 54
) {
    val showScreenName = activeContentLabel.isNotBlank() && activeContentLabel != "Home"

    Div({
        style {
            if (absolute) {
                position(Position.Absolute)
                left(50.percent)
                top(44.percent)
                property("transform", "translate(-50%, -50%)")
            }
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            gap(16.px)
            if (absolute) {
                minWidth(240.px)
                maxWidth(34.percent)
                overflow("hidden")
            } else {
                minWidth(0.px)
                property("max-width", "min(520px, 42vw)")
                overflow("hidden")
                flexShrink(1)
            }
            property("pointer-events", "auto")
            padding(if (absolute) 10.px else 0.px, 0.px, 0.px, 0.px)
        }
    }) {
        Img(LogoSettings.desktopDashboardSidebarLogo) {
            style {
                height(logoHeightPx.px)
                property("object-fit", "contain")
                cursor("pointer")
                flexShrink(0)
            }
            onClick { onHome() }
        }
        if (showScreenName) {
            Span({
                style {
                    color(DashboardTheme.TEXT_LIGHT)
                    fontSize(18.px)
                    fontWeight("700")
                    property("white-space", "nowrap")
                    if (absolute) {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    } else {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    }
                    minWidth(0.px)
                }
            }) { Text(activeContentLabel) }
        }
    }
}

@Composable
private fun DashboardNetScreenNav(
    screens: List<All>,
    selectedKey: String,
    openTopbarScreens: List<String>,
    showNavigationIcons: Boolean,
    horizontal: Boolean,
    underline: Boolean = false,
    hoverPanelRows: Boolean = false,
    onOpenScreen: (All) -> Unit
) {
    val contentScreens = screens.filter { it.screenMode != ScreenMode.POPUP }

    Div({
        style {
            position(Position.Relative)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Stretch)
            gap(if (horizontal) 6.px else 4.px)
            minWidth(0.px)
            minHeight(24.px)
            flexShrink(0)
            property("width", if (horizontal) "max-content" else "100%")
            if (!horizontal) {
                property("box-sizing", "border-box")
            }
            overflow("visible")
            padding(0.px, 0.px, 0.px, 0.px)
        }
    }) {
        if (contentScreens.isNotEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(if (horizontal) FlexDirection.Row else FlexDirection.Column)
                    alignItems(AlignItems.Stretch)
                    gap(if (horizontal) 22.px else 4.px)
                    minWidth(0.px)
                    property("width", if (horizontal) "max-content" else "100%")
                    overflowX(if (horizontal) "auto" else "visible")
                    overflowY("visible")
                }
            }) {
                contentScreens.forEach { screen ->
                    DashboardNetNavItem(
                        label = screen.label.ifBlank { screen.key },
                        iconPath = screen.icon.ifBlank { "images/step.svg" },
                        showIcon = showNavigationIcons,
                        active = screen.key == selectedKey || screen.key in openTopbarScreens,
                        fontSizePx = 13,
                        underline = underline,
                        hoverPanelRow = hoverPanelRows,
                        markHover = true,
                        colorHoverText = true,
                        onClick = { onOpenScreen(screen) }
                    )
                }
            }
        }
    }
}

@Composable
private fun DashboardNetPopupNavItem(
    label: String,
    iconPath: String,
    active: Boolean,
    showHoverLabel: Boolean = true,
    onClick: () -> Unit
) {
    var hovered by remember { mutableStateOf(false) }

    Div({
        style {
            position(Position.Relative)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            width(24.px)
            height(24.px)
            overflow("visible")
        }
    }) {
        Button({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                width(24.px)
                height(24.px)
                padding(0.px)
                backgroundColor(Color.transparent)
                border(0.px)
                borderRadius(0.px)
                cursor("pointer")
                property("outline", "none")
            }
            onMouseEnter { hovered = true }
            onMouseLeave { hovered = false }
            onClick { onClick() }
        }) {
            Img(src = iconPath) {
                style {
                    width(18.px)
                    height(18.px)
                    flexShrink(0)
                    property("pointer-events", "none")
                    property("filter", if (active || hovered) DashboardTheme.ICON_FILTER_ACTIVE else DashboardTheme.ICON_FILTER_WHITE)
                    property("transition", "filter 0.18s ease")
                }
            }
        }
        if (hovered && showHoverLabel) {
            Div({
                style {
                    position(Position.Absolute)
                    bottom(28.px)
                    left(50.percent)
                    property("transform", "translateX(-50%)")
                    padding(6.px, 9.px)
                    property("background", DashboardTheme.PREMIUM_SURFACE_BG)
                    property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                    property("border-radius", "8px")
                    property("box-shadow", "0 10px 24px ${DashboardTheme.SURFACE_SHADOW}")
                    color(DashboardTheme.TEXT_LIGHT)
                    fontSize(12.px)
                    fontWeight("600")
                    property("white-space", "nowrap")
                    property("z-index", DashboardTheme.NAVIGATION_POPUP_Z_INDEX.toString())
                }
            }) { Text(label) }
        }
    }
}

@Composable
private fun DashboardNetNavItem(
    label: String,
    iconPath: String,
    showIcon: Boolean,
    active: Boolean,
    fontSizePx: Int,
    underline: Boolean,
    hoverPanelRow: Boolean = false,
    markHover: Boolean = false,
    colorHoverText: Boolean = false,
    onClick: () -> Unit
) {
    var hovered by remember { mutableStateOf(false) }
    val highlighted = active || hovered

    Button({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Row)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(7.px)
            if (hoverPanelRow) {
                width(100.percent)
                property("align-self", "stretch")
                property("box-sizing", "border-box")
                justifyContent(JustifyContent.FlexStart)
                padding(9.px, 14.px)
            } else {
                padding(if (underline) 9.px else 4.px, 2.px)
            }
            backgroundColor(Color.transparent)
            border(0.px)
            property(
                "border-bottom",
                if (underline && active) "2px solid ${DashboardTheme.ACCENT}" else "2px solid transparent"
            )
            borderRadius(0.px)
            cursor("pointer")
            color(if (colorHoverText && (hovered || active)) DashboardTheme.ACCENT else DashboardTheme.TEXT_LIGHT)
            fontSize(fontSizePx.px)
            fontWeight("500")
            lineHeight("1.1")
            property("white-space", "nowrap")
            property("outline", "none")
            property("transition", "background 0.14s ease, color 0.18s ease, border-color 0.18s ease")
            if (markHover && (hovered || active)) {
                backgroundColor(UISettings.TABULAR_HOVER_BG)
            }
        }
        onMouseEnter { hovered = true }
        onMouseLeave { hovered = false }
        onClick { onClick() }
    }) {
        if (showIcon) {
            Img(src = iconPath) {
                style {
                    width(18.px)
                    height(18.px)
                    flexShrink(0)
                    property("pointer-events", "none")
                    property("filter", DashboardTheme.ICON_FILTER_WHITE)
                }
            }
        }
        Span { Text(label) }
    }
}
